import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import {
  type DashboardChange,
  type DashboardCoverage,
  type DashboardPeriod,
  type DashboardRate,
  getDashboardSummary,
} from "@/services/Home.service";
import { Storage } from "@/services/storage-services";
import { useQuery } from "@tanstack/react-query";
import { AlertTriangle, ArrowRight, Info, RefreshCw } from "lucide-react";
import { useState } from "react";
import { BsFillPatchCheckFill } from "react-icons/bs";

const PERIOD_OPTIONS: { label: string; value: DashboardPeriod }[] = [
  { label: "All Time", value: "all_time" },
  { label: "Today", value: "today" },
  { label: "Last 7 Days", value: "7_days" },
  { label: "Last 30 Days", value: "30_days" },
  { label: "Last 6 Months", value: "6_months" },
  { label: "Last 1 Year", value: "1_year" },
];

const COMPARISON_LABELS: Record<DashboardPeriod, string> = {
  today: "vs previous day",
  "7_days": "vs previous 7 days",
  "30_days": "vs previous 30 days",
  "6_months": "vs previous 6 months",
  "1_year": "vs previous year",
  all_time: "No comparable period",
};

function formatNgn(value: string | null): string {
  if (value === null || !Number.isFinite(Number(value))) return "Unavailable";
  return new Intl.NumberFormat("en-NG", {
    style: "currency",
    currency: "NGN",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(Number(value));
}

function formatMargin(rate: DashboardRate): string {
  return rate.marginType === "FLAT"
    ? `₦${rate.marginValue} / $1`
    : `${rate.marginValue}%`;
}

function formatPercentage(value: string | null): string | null {
  if (value === null || !Number.isFinite(Number(value))) return null;
  return new Intl.NumberFormat("en-US", {
    maximumFractionDigits: 2,
  }).format(Number(value));
}

function formatTimestamp(value: string | null): string {
  if (!value) return "Rate unavailable";
  return `Updated ${new Date(value).toLocaleString("en-NG", {
    dateStyle: "medium",
    timeStyle: "short",
  })}`;
}

function formatBoundary(value: string | null): string {
  if (!value) return "Not applicable";
  return new Date(value).toLocaleString("en-NG", {
    dateStyle: "medium",
    timeStyle: "short",
    timeZone: "UTC",
  });
}

function TrendBadge({ change }: { change: DashboardChange }) {
  if (change.trend === "UNAVAILABLE") return null;
  if (change.trend === "NEW") {
    return (
      <span className="inline-flex h-6 items-center rounded-full bg-[#FFF5D6] px-2.5 text-xs font-medium text-[#7A5A00]">
        New
      </span>
    );
  }

  const percentage = formatPercentage(change.percentage);
  const isUp = change.trend === "UP";
  const isDown = change.trend === "DOWN";
  const label =
    percentage === null ? "No change" : `${isUp ? "+" : ""}${percentage}%`;

  return (
    <span
      className={`inline-flex h-6 items-center rounded-full px-2.5 text-xs font-medium ${
        isUp
          ? "bg-green-0 text-green-2"
          : isDown
            ? "bg-red-50 text-red-700"
            : "bg-[#F3F0F3] text-gray-70"
      }`}
    >
      {label}
    </span>
  );
}

function ActivityCard({
  title,
  value,
  change,
  period,
  financial = false,
}: {
  title: string;
  value: string;
  change: DashboardChange;
  period: DashboardPeriod;
  financial?: boolean;
}) {
  const comparison =
    change.trend === "UNAVAILABLE"
      ? "No comparable period"
      : COMPARISON_LABELS[period];

  return (
    <Card className="flex min-h-[148px] flex-col justify-between rounded-xl border-off-white p-5 shadow-sm">
      <div className="min-w-0">
        <p className="text-sm font-medium text-primary-4">{title}</p>
        <p
          className={`mt-3 break-words font-semibold leading-tight text-primary-1 ${
            financial ? "text-xl" : "text-3xl"
          }`}
        >
          {value}
        </p>
      </div>
      <div className="mt-4 flex flex-wrap items-center gap-2 border-t border-border pt-3">
        <TrendBadge change={change} />
        <span className="text-xs text-gray-60">{comparison}</span>
      </div>
    </Card>
  );
}

function RateCard({ label, rate }: { label: string; rate: DashboardRate }) {
  return (
    <Card className="rounded-xl border-off-white p-5 shadow-sm">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="text-sm font-semibold text-primary-3">{label}</p>
        <span className="rounded-full bg-[#F3F0F3] px-2.5 py-1 text-xs font-medium text-primary-3">
          {formatMargin(rate)}
        </span>
      </div>
      <div className="mt-4 grid grid-cols-[minmax(0,1fr)_auto_minmax(0,1fr)] items-center gap-3">
        <div className="min-w-0">
          <p className="text-xs text-gray-60">Provider</p>
          <p className="mt-1 break-words text-base font-semibold text-primary-3">
            {formatNgn(rate.providerRate)}
          </p>
        </div>
        <ArrowRight className="h-4 w-4 shrink-0 text-gray-50" />
        <div className="min-w-0">
          <p className="text-xs text-gray-60">Customer</p>
          <p className="mt-1 break-words text-base font-semibold text-primary-3">
            {formatNgn(rate.customerRate)}
          </p>
        </div>
      </div>
      <p className="mt-3 text-xs text-gray-60">{formatTimestamp(rate.asOf)}</p>
    </Card>
  );
}

function coverageText(coverage: DashboardCoverage): string {
  const total = coverage.includedTransactions + coverage.excludedTransactions;
  if (total === 0) return "No completed activity";
  if (coverage.includedTransactions === 0) return "No snapshot coverage";
  if (coverage.excludedTransactions > 0) {
    return `Based on ${coverage.includedTransactions} of ${total} completed transactions`;
  }
  const suffix =
    coverage.includedTransactions === 1 ? "transaction" : "transactions";
  return `${coverage.includedTransactions} snapshot-backed ${suffix}`;
}

function UnavailableSpreadInfo() {
  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          type="button"
          aria-label="Why this pricing spread is unavailable"
          className="rounded-full text-gray-60 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        >
          <Info className="h-3.5 w-3.5" />
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-64 p-3 text-xs leading-5">
        Reliable pricing snapshots are not available for these historical
        transactions.
      </PopoverContent>
    </Popover>
  );
}

function SpreadRow({
  label,
  value,
  coverage,
  emphasized = false,
}: {
  label: string;
  value: string | null;
  coverage?: DashboardCoverage;
  emphasized?: boolean;
}) {
  const isNegative = value !== null && Number(value) < 0;
  return (
    <div
      className={`flex flex-col gap-2 py-3 sm:flex-row sm:items-center sm:justify-between ${
        emphasized
          ? "mt-1 rounded-lg bg-[#FFF9E8] px-3"
          : "border-b border-border"
      }`}
    >
      <div className="min-w-0">
        <p className="text-sm font-medium leading-5 text-primary-3">{label}</p>
        {coverage ? (
          <p className="mt-0.5 text-xs text-gray-60">
            {coverageText(coverage)}
          </p>
        ) : null}
      </div>
      <div className="flex shrink-0 items-center gap-1.5">
        <p
          className={`text-lg font-semibold ${
            isNegative ? "text-red-700" : "text-primary-1"
          }`}
        >
          {formatNgn(value)}
        </p>
        {value === null ? <UnavailableSpreadInfo /> : null}
      </div>
    </div>
  );
}

function CustomerCard({ title, value }: { title: string; value: number }) {
  return (
    <Card className="flex min-h-[132px] flex-col justify-between rounded-xl border-off-white p-5 shadow-sm">
      <div>
        <p className="text-sm font-medium text-primary-4">{title}</p>
        <p className="mt-3 text-3xl font-semibold text-primary-1">
          {value.toLocaleString("en-US")}
        </p>
      </div>
      <p className="mt-4 border-t border-border pt-3 text-xs text-gray-60">
        Current state
      </p>
    </Card>
  );
}

const Home = () => {
  const [period, setPeriod] = useState<DashboardPeriod>("all_time");
  const merchant = Storage.getItem("selectedMerchant");
  const initial = merchant?.businessName?.substring(0, 1);
  const summary = useQuery({
    queryKey: ["dashboardSummary", period],
    queryFn: () => getDashboardSummary(period),
    placeholderData: (previousData) => previousData,
    refetchOnMount: "always",
    retry: 1,
  });

  if (summary.isError && !summary.data) {
    return (
      <div className="grid min-h-[420px] place-items-center p-6">
        <div className="max-w-md text-center">
          <AlertTriangle className="mx-auto h-8 w-8 text-red-600" />
          <h1 className="mt-4 text-xl font-semibold text-primary-3">
            Unable to load dashboard insights
          </h1>
          <p className="mt-2 text-sm text-gray-70">
            The latest business metrics could not be retrieved. Try again.
          </p>
          <Button
            className="mt-5 rounded-lg"
            onClick={() => void summary.refetch()}
          >
            <RefreshCw className="mr-2 h-4 w-4" />
            Try again
          </Button>
        </div>
      </div>
    );
  }

  if (!summary.data) {
    return (
      <div className="grid gap-7 p-4 py-7 sm:p-6 sm:py-8">
        <Skeleton className="h-14 w-72" />
        <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
          {Array.from({ length: 4 }).map((_, index) => (
            <Skeleton key={index} className="h-[148px] rounded-xl" />
          ))}
        </div>
        <div className="grid gap-5 xl:grid-cols-2">
          <Skeleton className="h-64 rounded-xl" />
          <Skeleton className="h-72 rounded-xl" />
        </div>
      </div>
    );
  }

  const data = summary.data;
  const onRamp = data.transactions.onRamp;
  const offRamp = data.transactions.offRamp;
  const isUpdating = summary.isFetching;

  return (
    <>
      <div
        className="grid gap-7 p-4 py-7 sm:p-6 sm:py-8"
        aria-busy={isUpdating}
      >
        <header className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex gap-4">
            {merchant === null ? (
              <Skeleton className="h-14 w-14" />
            ) : (
              <Avatar className="h-14 w-14">
                <AvatarImage />
                <AvatarFallback>{initial}</AvatarFallback>
              </Avatar>
            )}
            <div>
              <p className="text-2xl text-primary-3">
                Hi,{" "}
                <span className="font-semibold">{merchant?.businessName}</span>
              </p>
              <p className="mt-1 flex gap-2 text-base capitalize text-gray">
                <BsFillPatchCheckFill className="h-5 w-5 text-green" />
                {merchant?.roleId?.split("-")[1]}
              </p>
            </div>
          </div>

          <div className="w-full sm:w-52">
            <Select
              value={period}
              onValueChange={(value) => setPeriod(value as DashboardPeriod)}
            >
              <SelectTrigger className="w-full rounded-lg">
                <SelectValue placeholder="Reporting period" />
              </SelectTrigger>
              <SelectContent>
                {PERIOD_OPTIONS.map((option) => (
                  <SelectItem key={option.value} value={option.value}>
                    {option.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <div className="mt-1.5 flex items-center justify-end gap-2 text-xs text-gray-60">
              {isUpdating ? <span>Updating…</span> : null}
              <Popover>
                <PopoverTrigger asChild>
                  <button
                    type="button"
                    className="inline-flex items-center gap-1 rounded-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                  >
                    <Info className="h-3.5 w-3.5" /> UTC
                  </button>
                </PopoverTrigger>
                <PopoverContent
                  align="end"
                  className="w-80 p-3 text-xs leading-5"
                >
                  <p>
                    Current: {formatBoundary(data.period.start)} —{" "}
                    {formatBoundary(data.period.end)}
                  </p>
                  {data.period.previousStart && data.period.previousEnd ? (
                    <p>
                      Previous: {formatBoundary(data.period.previousStart)} —{" "}
                      {formatBoundary(data.period.previousEnd)}
                    </p>
                  ) : (
                    <p>No previous period applies.</p>
                  )}
                </PopoverContent>
              </Popover>
            </div>
          </div>
        </header>

        {summary.isError ? (
          <div className="flex items-center gap-2 rounded-lg bg-red-50 px-3 py-2 text-xs text-red-700">
            <AlertTriangle className="h-4 w-4 shrink-0" />
            This period could not be refreshed. The last available dashboard
            data is still shown.
          </div>
        ) : null}

        <section>
          <h1 className="mb-3 text-base font-semibold text-primary-3">
            Activity
          </h1>
          <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
            <ActivityCard
              title="On-Ramp Transactions"
              value={onRamp.count.toLocaleString("en-US")}
              change={onRamp.change}
              period={data.period.key}
            />
            <ActivityCard
              title="Off-Ramp Transactions"
              value={offRamp.count.toLocaleString("en-US")}
              change={offRamp.change}
              period={data.period.key}
            />
            <ActivityCard
              title="On-Ramp Customer Volume"
              value={formatNgn(onRamp.volumeNgn)}
              change={onRamp.volumeChange}
              period={data.period.key}
              financial
            />
            <ActivityCard
              title="Off-Ramp Customer Payout Volume"
              value={formatNgn(offRamp.volumeNgn)}
              change={offRamp.volumeChange}
              period={data.period.key}
              financial
            />
          </div>
        </section>

        <div className="grid items-start gap-5 xl:grid-cols-5">
          <section className="rounded-xl border border-off-white bg-card p-5 shadow-sm xl:col-span-3">
            <h2 className="text-base font-semibold text-primary-3">
              Current Exchange Rates
            </h2>
            <div className="mt-3 grid gap-4 md:grid-cols-2">
              <RateCard label="On-Ramp — Quidax" rate={data.rates.onRamp} />
              <RateCard label="Off-Ramp — Treasura" rate={data.rates.offRamp} />
            </div>
          </section>

          <section className="rounded-xl border border-off-white bg-card p-5 shadow-sm xl:col-span-2">
            <div className="flex items-center gap-2">
              <h2 className="text-base font-semibold text-primary-3">
                Pricing Spread
              </h2>
              <Popover>
                <PopoverTrigger asChild>
                  <button
                    type="button"
                    aria-label="How pricing spread is represented"
                    className="rounded-full text-gray-60 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                  >
                    <Info className="h-4 w-4" />
                  </button>
                </PopoverTrigger>
                <PopoverContent className="w-80 p-3 text-xs leading-5">
                  On-ramp spread is estimated from pricing snapshots. Off-ramp
                  retained spread is before provider fees. Pricing spread is not
                  held in a separate profit wallet.
                </PopoverContent>
              </Popover>
            </div>
            <div className="mt-1">
              <SpreadRow
                label="Estimated On-Ramp Pricing Spread"
                value={data.pricingSpread.estimatedOnRampNgn}
                coverage={data.pricingSpread.coverage.onRamp}
              />
              <SpreadRow
                label="Off-Ramp Configured Pricing Spread"
                value={data.pricingSpread.offRampConfiguredNgn}
              />
              <SpreadRow
                label="Off-Ramp Retained Spread Before Provider Fees"
                value={data.pricingSpread.offRampRetainedBeforeProviderFeesNgn}
                coverage={data.pricingSpread.coverage.offRamp}
              />
              {data.pricingSpread.estimatedTotalNgn !== null ? (
                <SpreadRow
                  label="Estimated Total Pricing Spread"
                  value={data.pricingSpread.estimatedTotalNgn}
                  emphasized
                />
              ) : null}
            </div>
            <div className="mt-3 flex items-start gap-2 text-xs leading-5 text-gray-60">
              <Info className="mt-0.5 h-3.5 w-3.5 shrink-0" />
              <p>
                Derived insight—not available cash or net profit; provider fees
                are not fully deducted.
              </p>
            </div>
          </section>
        </div>

        <section>
          <h2 className="mb-3 text-base font-semibold text-primary-3">
            Customers
          </h2>
          <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
            <CustomerCard
              title="Total Customers"
              value={data.customers.total}
            />
            <CustomerCard
              title="Verified Customers"
              value={data.customers.verified}
            />
            <CustomerCard
              title="Active Customers"
              value={data.customers.active}
            />
            <CustomerCard
              title="Suspended Customers"
              value={data.customers.suspended}
            />
          </div>
        </section>
      </div>
    </>
  );
};

export default Home;
